import React, { useState } from 'react'
import { Link } from '@inertiajs/react'
import Layout from '../../components/Layout'

function Accordion({ title, children, defaultOpen = false }) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div className="border border-gray-200 rounded-lg mb-4">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 rounded-t-lg transition-colors"
      >
        <span className="font-semibold text-brand-dark">{title}</span>
        <svg
          className={`w-5 h-5 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="p-4 overflow-auto max-h-[440px]">
          {children}
        </div>
      )}
    </div>
  )
}

function ResponsibilityTable({ items, linkPrefix, columns }) {
  if (items.length === 0) {
    return <p className="text-gray-500 italic">Nenhum item</p>
  }

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-gray-200">
          {columns.map((col) => (
            <th key={col.key} className="text-left py-2 px-3 font-medium text-gray-600">
              {col.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
            {columns.map((col) => (
              <td key={col.key} className="py-2 px-3">
                {col.key === 'link' ? (
                  <Link href={`${linkPrefix}/${item.id}`} className="link">
                    {item.nome || item.polo_nome || `#${item.id}`}
                  </Link>
                ) : col.key === 'status' ? (
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                    item.status === 'revisado' ? 'bg-green-100 text-green-700' :
                    item.status === 'inserido' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {item.status || 'pendente'}
                  </span>
                ) : (
                  item[col.key]
                )}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export default function Dashboard({
  user,
  responsible_polos,
  responsible_quadras,
  responsible_lotes,
  revisor_polos,
  revisor_quadras,
  revisor_lotes,
}) {
  const poloColumns = [
    { key: 'link', label: 'Nome' },
    { key: 'status', label: 'Status' },
  ]

  const quadraColumns = [
    { key: 'link', label: 'Polo' },
    { key: 'status', label: 'Status' },
  ]

  const loteColumns = [
    { key: 'link', label: 'Quadra' },
    { key: 'status', label: 'Status' },
  ]

  const hasResponsibilities = (responsible_polos?.length > 0) ||
    (responsible_quadras?.length > 0) ||
    (responsible_lotes?.length > 0)

  const hasRevisorTasks = (revisor_polos?.length > 0) ||
    (revisor_quadras?.length > 0) ||
    (revisor_lotes?.length > 0)

  return (
    <Layout>
      <div className="py-8">
        <h1 className="text-2xl font-bold text-brand-dark mb-2 font-heading">
          Bem-vindo, {user?.nome || 'Usuário'}!
        </h1>
        <p className="text-gray-600 mb-6">
          {user?.email} - {user?.permission || 'visitor'}
        </p>

        {/* Edit Profile Button - First */}
        <div className="mb-8">
          <Link
            href="/users/edit"
            className="inline-block px-5 py-2.5 bg-brand-dark hover:bg-brand-light text-white font-medium rounded transition-colors"
          >
            Editar Perfil
          </Link>
        </div>

        {/* Responsibilities Section */}
        <h2 className="text-xl font-bold text-brand-dark mb-4 font-heading">Responsável por adicionar</h2>

        {!hasResponsibilities ? (
          <p className="text-gray-500 italic mb-8">Você não possui responsabilidades de adição no momento.</p>
        ) : (
          <div className="mb-8">
            {(responsible_polos?.length > 0) && (
              <Accordion title={`Polos (${responsible_polos.length})`} defaultOpen>
                <ResponsibilityTable items={responsible_polos} linkPrefix="/polos" columns={poloColumns} />
              </Accordion>
            )}

            {(responsible_quadras?.length > 0) && (
              <Accordion title={`Quadras (${responsible_quadras.length})`}>
                <ResponsibilityTable items={responsible_quadras} linkPrefix="/quadras" columns={quadraColumns} />
              </Accordion>
            )}

            {(responsible_lotes?.length > 0) && (
              <Accordion title={`Lotes (${responsible_lotes.length})`}>
                <ResponsibilityTable items={responsible_lotes} linkPrefix="/lotes" columns={loteColumns} />
              </Accordion>
            )}
          </div>
        )}

        {/* Revisor Section */}
        <h2 className="text-xl font-bold text-brand-dark mb-4 font-heading">Responsável como Revisor</h2>

        {!hasRevisorTasks ? (
          <p className="text-gray-500 italic">Você não possui responsabilidades de revisão no momento.</p>
        ) : (
          <div>
            {(revisor_polos?.length > 0) && (
              <Accordion title={`Polos (${revisor_polos.length})`} defaultOpen>
                <ResponsibilityTable items={revisor_polos} linkPrefix="/polos" columns={poloColumns} />
              </Accordion>
            )}

            {(revisor_quadras?.length > 0) && (
              <Accordion title={`Quadras (${revisor_quadras.length})`}>
                <ResponsibilityTable items={revisor_quadras} linkPrefix="/quadras" columns={quadraColumns} />
              </Accordion>
            )}

            {(revisor_lotes?.length > 0) && (
              <Accordion title={`Lotes (${revisor_lotes.length})`}>
                <ResponsibilityTable items={revisor_lotes} linkPrefix="/lotes" columns={loteColumns} />
              </Accordion>
            )}
          </div>
        )}
      </div>
    </Layout>
  )
}
